// src/features/subscription/PlanFormDialog.tsx
// Create / edit a subscription plan — one form-kit dialog shared by both
// flows (docs/ux/14-forms.md), opened from PlanListPage's "+ New" and from
// PlanDetailPage's "Edit" button. Money fields (price, setup fee) are
// entered/displayed in major units ("9.99") and converted to minor units on
// submit, same convention as GrantCreditModal
// (src/features/billing/GrantCreditModal.tsx). Slug and the active flag are
// only settable once the plan exists (CreatePlanRequest doesn't carry them —
// the server assigns the initial slug from the name).

import { useEffect, useMemo, useState } from 'react';
import { observer } from '@react';
import { useSubscriptionPlan, useI18n } from '@react/hooks';
import { Alert } from '@react/components';
import { FormDialog, FormRow, TitleInput, AutoGrowTextarea } from '@/components/form';
import { Select } from '@/components/Select';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { errMessage, toMajorUnitsText, toMinorUnits } from './format';
import type { SubscriptionPlan } from '@core/types/subscription/SubscriptionPlan';
import type { PlanType } from '@core/types/subscription/PlanType';
import type { Currency } from '@core/types/subscription/Currency';
import type { BillingCycle } from '@core/types/subscription/BillingCycle';

const PLAN_TYPES: PlanType[] = ['PLAN_TYPE_FREE', 'PLAN_TYPE_BASIC', 'PLAN_TYPE_PROFESSIONAL', 'PLAN_TYPE_ENTERPRISE', 'PLAN_TYPE_CUSTOM'];
const CURRENCIES: Currency[] = ['CURRENCY_USD', 'CURRENCY_EUR', 'CURRENCY_RUB', 'CURRENCY_GBP', 'CURRENCY_JPY', 'CURRENCY_CNY', 'CURRENCY_KZT', 'CURRENCY_BYN', 'CURRENCY_AED', 'CURRENCY_TRY'];
const BILLING_CYCLES: BillingCycle[] = ['BILLING_CYCLE_MONTHLY', 'BILLING_CYCLE_QUARTERLY', 'BILLING_CYCLE_SEMI_ANNUALLY', 'BILLING_CYCLE_ANNUALLY', 'BILLING_CYCLE_DAILY', 'BILLING_CYCLE_WEEKLY', 'BILLING_CYCLE_BIWEEKLY', 'BILLING_CYCLE_CUSTOM'];

function digits(v: string): string {
  return v.replace(/[^0-9]/g, '');
}

function decimalText(v: string): string | null {
  return /^\d*\.?\d{0,2}$/.test(v) ? v : null;
}

interface FormState {
  name: string;
  description: string;
  type: PlanType;
  price: string; // major units text
  currency: Currency;
  billing_cycle: BillingCycle;
  trial_period_days: string;
  setup_fee: string; // major units text
  is_public: boolean;
  slug: string;
  is_active: boolean;
}

function snapshotOf(plan: SubscriptionPlan | null): FormState {
  return {
    name: plan?.name ?? '',
    description: plan?.description ?? '',
    type: plan?.type ?? 'PLAN_TYPE_BASIC',
    price: toMajorUnitsText(plan?.price),
    currency: plan?.currency ?? 'CURRENCY_USD',
    billing_cycle: plan?.billing_cycle ?? 'BILLING_CYCLE_MONTHLY',
    trial_period_days: plan?.trial_period_days ? String(plan.trial_period_days) : '',
    setup_fee: toMajorUnitsText(plan?.setup_fee),
    is_public: plan?.is_public ?? false,
    slug: plan?.slug ?? '',
    is_active: plan?.is_active ?? true,
  };
}

export interface PlanFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** Plan being edited; null → create. */
  plan: SubscriptionPlan | null;
  onSaved: () => void;
}

export const PlanFormDialog = observer(({ open, onClose, plan, onSaved }: PlanFormDialogProps) => {
  const { t } = useI18n();
  const store = useSubscriptionPlan();
  const isEdit = !!plan;

  const [form, setForm] = useState<FormState>(() => snapshotOf(plan));
  const [initial, setInitial] = useState<FormState>(() => snapshotOf(plan));
  const [moreFields, setMoreFields] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const snap = snapshotOf(plan);
    setForm(snap);
    setInitial(snap);
    setMoreFields(false);
    setError(null);
  }, [open, plan]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(initial), [form, initial]);
  const canSubmit = form.name.trim().length > 0;

  const typeOptions = PLAN_TYPES.map((v) => ({ value: v, label: t(`subscription.plan.type.${v.replace('PLAN_TYPE_', '').toLowerCase()}`) }));
  const currencyOptions = CURRENCIES.map((v) => ({ value: v, label: t(`subscription.currency.${v.replace('CURRENCY_', '').toLowerCase()}`) }));
  const billingCycleOptions = BILLING_CYCLES.map((v) => ({ value: v, label: t(`subscription.billingCycle.${v.replace('BILLING_CYCLE_', '').toLowerCase()}`) }));

  const submit = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);
    setError(null);
    try {
      if (isEdit && plan) {
        await store.UpdatePlan(plan.entity?.id ?? '', {
          plan: {
            ...plan,
            name: form.name.trim(),
            description: form.description,
            type: form.type,
            slug: form.slug.trim(),
            is_active: form.is_active,
            is_public: form.is_public,
            price: toMinorUnits(form.price),
            currency: form.currency,
            billing_cycle: form.billing_cycle,
            trial_period_days: form.trial_period_days ? Number(form.trial_period_days) : 0,
            setup_fee: toMinorUnits(form.setup_fee),
          },
          field_mask: ['name', 'description', 'type', 'slug', 'is_active', 'is_public', 'price', 'currency', 'billing_cycle', 'trial_period_days', 'setup_fee'],
        });
      } else {
        await store.CreatePlan({
          name: form.name.trim(),
          description: form.description,
          type: form.type,
          price: toMinorUnits(form.price),
          currency: form.currency,
          billing_cycle: form.billing_cycle,
          trial_period_days: form.trial_period_days ? Number(form.trial_period_days) : 0,
          setup_fee: toMinorUnits(form.setup_fee),
          is_public: form.is_public,
        });
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(errMessage(e, t('subscription.plan.saveError')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={isEdit ? t('common.saveChanges') : t('subscription.plan.create')}
      canSubmit={canSubmit}
      isSubmitting={saving}
      dirty={dirty}
      width="lg"
      aria-label={isEdit ? t('subscription.plan.edit') : t('subscription.plan.create')}
      footerLeft={
        <button type="button" onClick={() => setMoreFields((v) => !v)} className="text-xs text-muted-foreground hover:text-foreground">
          {moreFields ? t('formKit.fewerFields') : t('formKit.moreFields')}
        </button>
      }
    >
      <div className="space-y-1 px-3.5 pb-2 pt-2">
        {error && <Alert variant="error">{error}</Alert>}
        <TitleInput
          value={form.name}
          onChange={(v) => set('name', v)}
          placeholder={`${t('subscription.plan.field.name')}…`}
          initialFocus
          aria-label={t('subscription.plan.field.name')}
        />
        <AutoGrowTextarea
          value={form.description}
          onChange={(v) => set('description', v)}
          placeholder={`${t('subscription.plan.field.description')}…`}
          minRows={1}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2 px-3.5 pb-3">
        <div className="w-40">
          <Select value={form.type} onChange={(e) => set('type', e.target.value as PlanType)} options={typeOptions} aria-label={t('subscription.plan.field.type')} fullWidth />
        </div>
        <div className="w-28">
          <Select value={form.currency} onChange={(e) => set('currency', e.target.value as Currency)} options={currencyOptions} aria-label={t('subscription.plan.field.currency')} fullWidth />
        </div>
        <div className="w-36">
          <Select value={form.billing_cycle} onChange={(e) => set('billing_cycle', e.target.value as BillingCycle)} options={billingCycleOptions} aria-label={t('subscription.plan.field.billingCycle')} fullWidth />
        </div>
        <label className="flex items-center gap-1.5 text-sm text-foreground">
          <input type="checkbox" checked={form.is_public} onChange={(e) => set('is_public', e.target.checked)} className="h-4 w-4 rounded border-input" />
          {t('subscription.plan.field.isPublic')}
        </label>
      </div>

      <div className="flex flex-wrap gap-2 px-3.5 pb-3">
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-muted px-2.5 py-1">
          <span className="text-xs text-muted-foreground">{t('subscription.plan.field.price')}</span>
          <input
            type="text"
            inputMode="decimal"
            value={form.price}
            onChange={(e) => { const v = decimalText(e.target.value); if (v !== null) set('price', v); }}
            placeholder="0.00"
            aria-label={t('subscription.plan.field.price')}
            className="w-20 bg-transparent text-right text-sm tabular-nums text-foreground outline-none"
          />
          <span className="text-xs text-muted-foreground">{form.currency.replace('CURRENCY_', '')}</span>
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-muted px-2.5 py-1">
          <span className="text-xs text-muted-foreground">{t('subscription.plan.field.setupFee')}</span>
          <input
            type="text"
            inputMode="decimal"
            value={form.setup_fee}
            onChange={(e) => { const v = decimalText(e.target.value); if (v !== null) set('setup_fee', v); }}
            placeholder="0.00"
            aria-label={t('subscription.plan.field.setupFee')}
            className="w-20 bg-transparent text-right text-sm tabular-nums text-foreground outline-none"
          />
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-muted px-2.5 py-1">
          <span className="text-xs text-muted-foreground">{t('subscription.plan.field.trialDays')}</span>
          <input
            type="text"
            inputMode="numeric"
            value={form.trial_period_days}
            onChange={(e) => set('trial_period_days', digits(e.target.value))}
            placeholder="0"
            aria-label={t('subscription.plan.field.trialDays')}
            className="w-12 bg-transparent text-right text-sm tabular-nums text-foreground outline-none"
          />
        </span>
      </div>

      {moreFields && isEdit && (
        <div className="space-y-1 border-t border-border px-3.5 py-2">
          <FormRow label={t('subscription.plan.field.slug')} labelWidth="sm">
            <input
              value={form.slug}
              onChange={(e) => set('slug', e.target.value)}
              aria-label={t('subscription.plan.field.slug')}
              className="w-full max-w-xs rounded-md border border-input bg-background px-2.5 py-1.5 font-mono text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
            />
          </FormRow>
          <ToggleSwitch label={t('subscription.plan.field.isActive')} checked={form.is_active} onChange={(v) => set('is_active', v)} />
        </div>
      )}
      {moreFields && !isEdit && (
        <div className="border-t border-border px-3.5 py-2 text-xs text-muted-foreground">
          {t('subscription.plan.slugAfterCreateHint')}
        </div>
      )}
    </FormDialog>
  );
});

export default PlanFormDialog;
